import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, X, Check } from "@phosphor-icons/react";

const ROLES = ["admin", "sales", "approver"];

function RolePill({ role }) {
  const color = { admin: "bg-slate-900 text-white", sales: "bg-blue-100 text-blue-800", approver: "bg-emerald-100 text-emerald-800" }[role] || "bg-slate-200";
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm ${color}`}>{role}</span>;
}

function UserFormModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [full_name, setFullName] = useState(initial?.full_name || "");
  const [email, setEmail] = useState(initial?.email || "");
  const [password, setPassword] = useState("");
  const [roles, setRoles] = useState(initial?.roles || ["sales"]);
  const [busy, setBusy] = useState(false);

  const toggle = (r) => setRoles(roles.includes(r) ? roles.filter(x => x !== r) : [...roles, r]);

  const save = async () => {
    if (!roles.length) return toast.error("Assign at least one role");
    if (!isEdit && !password) return toast.error("Password required");
    setBusy(true);
    try {
      if (isEdit) {
        await api.put(`/users/${initial.id}`, { full_name, roles, ...(password ? { password } : {}) });
        toast.success("User updated");
      } else {
        await api.post("/users", { email, full_name, password, roles });
        toast.success("User created");
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50" data-testid="user-modal">
      <div className="bg-white border border-slate-200 w-full max-w-md p-6 rounded-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold">{isEdit ? "Edit user" : "New user"}</h3>
          <button onClick={onClose} data-testid="user-modal-close"><X size={18}/></button>
        </div>
        <div className="space-y-3">
          <label className="block"><span className="label-micro">Full name</span>
            <input data-testid="user-fullname" value={full_name} onChange={e => setFullName(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"/></label>
          <label className="block"><span className="label-micro">Email</span>
            <input data-testid="user-email" type="email" disabled={isEdit} value={email} onChange={e => setEmail(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm disabled:bg-slate-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"/></label>
          <label className="block"><span className="label-micro">{isEdit ? "New password (optional)" : "Password"}</span>
            <input data-testid="user-password" type="password" value={password} onChange={e => setPassword(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"/></label>
          <div>
            <span className="label-micro">Roles (one or more)</span>
            <div className="mt-2 flex gap-2">
              {ROLES.map(r => (
                <button key={r} onClick={() => toggle(r)} data-testid={`role-toggle-${r}`}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-sm border ${roles.includes(r) ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 hover:border-blue-500"}`}>
                  {roles.includes(r) && <Check size={12} className="inline mr-1"/>}
                  {r.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="text-sm px-4 py-2 border border-slate-300 rounded-sm hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="user-save" className="text-sm px-4 py-2 bg-slate-900 text-white rounded-sm hover:bg-blue-700 transition-colors duration-200 disabled:opacity-60">
            {busy ? "Saving…" : (isEdit ? "Save" : "Create")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); }
    catch (e) { toast.error("Failed to load users"); }
  };
  useEffect(() => { load(); }, []);

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("User deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]" data-testid="users-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-micro">Admin</div>
          <h1 className="font-display text-3xl font-black mt-1">Users & Roles</h1>
        </div>
        <button onClick={() => setModal({})} data-testid="new-user-btn" className="inline-flex items-center gap-2 bg-slate-900 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-sm transition-colors duration-200">
          <Plus size={14}/> New user
        </button>
      </div>

      <div className="mt-6 bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left"><th className="px-4 py-2 label-micro">Name</th><th className="px-4 py-2 label-micro">Email</th><th className="px-4 py-2 label-micro">Roles</th><th className="px-4 py-2 label-micro">Created</th><th className="px-4 py-2 label-micro w-32">Actions</th></tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-b border-slate-100" data-testid={`user-row-${u.email}`}>
                <td className="px-4 py-3 font-semibold">{u.full_name}</td>
                <td className="px-4 py-3 text-slate-600">{u.email}</td>
                <td className="px-4 py-3"><div className="flex gap-1 flex-wrap">{u.roles.map(r => <RolePill key={r} role={r}/>)}</div></td>
                <td className="px-4 py-3 text-xs text-slate-500 tabular-nums">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <button onClick={() => setModal(u)} data-testid={`edit-user-${u.email}`} className="text-xs inline-flex items-center gap-1 border border-slate-300 rounded-sm px-2 py-1 hover:bg-slate-100 mr-2"><PencilSimple size={12}/>Edit</button>
                  <button onClick={() => del(u)} data-testid={`delete-user-${u.email}`} className="text-xs inline-flex items-center gap-1 border border-red-300 text-red-600 rounded-sm px-2 py-1 hover:bg-red-50"><Trash size={12}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && <UserFormModal initial={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }}/>}
    </div>
  );
}
